<script setup lang="ts">
import { bemBuilder, textBeautifier } from "@chatfood/core-utils";
import { ref, computed } from "vue";
import {
  useToast,
  AtomText,
  AtomIcon,
  AtomButton,
  AtomGraphic,
} from "@chatfood/design-system";
import type { IReviewPlatformsProps } from "./props";
import type { IReviewPlatformsEmits } from "./emits";
import { useLocale } from "~/composables/use-locale";

const css = bemBuilder("review-platforms");

const props = defineProps<IReviewPlatformsProps>();
const emits = defineEmits<IReviewPlatformsEmits>();

const platformLinkSelected = ref(props.platformLinks[0]);

const { t } = useLocale();
const { toastSuccess, toastError } = useToast();

const ctaLabel = computed(() => {
  if (!props.comment) {
    return t("components.review_platforms.cta_share");
  }

  return t("components.review_platforms.cta_share_with_comment");
});

const copyToClipboard = async (displayToast = false) => {
  if (!props.comment) return;

  try {
    await navigator.clipboard.writeText(props.comment);

    if (displayToast) {
      toastSuccess(t("components.review_platforms.comment_copied"));
    }
  } catch {
    toastError(t("components.review_platforms.copy_failure"));
  }
};

const redirectToExternalPage = () => {
  window.open(platformLinkSelected.value.url, "_blank", "noopener, noreferrer");
};

const handleShare = async () => {
  await copyToClipboard();
  redirectToExternalPage();
};

function onClose(event: MouseEvent) {
  emits("close", event);
}
</script>

<template>
  <div :class="css('')">
    <AtomText type="h2">
      {{ t("components.review_platforms.title") }}
    </AtomText>
    <AtomText tag="div" color="color-base-60">
      {{ t("components.review_platforms.subtitle") }}
    </AtomText>
    <div>
      <AtomText type="body-bold">
        {{ t("components.review_platforms.select_platform") }}
      </AtomText>
      <ul :class="css('platform')">
        <li
          v-for="(platform, index) in props.platformLinks"
          :key="index"
          data-test="platform"
          :class="css('platform-item')"
        >
          <a
            href=""
            :class="[
              css('platform-icon'),
              {
                [css('platform-icon', 'active')]:
                  platformLinkSelected.url === platform.url,
              },
            ]"
            @click.prevent="platformLinkSelected = platform"
          >
            <div>
              <AtomGraphic
                v-if="platform.type === 'google'"
                name="google"
                :size="64"
              />
              <AtomIcon v-else name="outlet" size="56" color="color-base-80" />
            </div>
            <AtomText
              :type="
                platformLinkSelected.url === platform.url
                  ? 'small-bold'
                  : 'small'
              "
            >
              {{ textBeautifier(platform.type) }}
            </AtomText>
          </a>
        </li>
      </ul>
    </div>
    <div v-if="props.comment" :class="css('comment')">
      <AtomText data-test="comment">{{ props.comment }}</AtomText>
      <a
        href=""
        data-test="btn-copy"
        :class="css('comment-copy')"
        @click.prevent="copyToClipboard(true)"
      >
        <AtomIcon name="copy" size="medium" />
      </a>
    </div>
    <div :class="css('ctas')">
      <AtomButton
        size="large"
        variant="secondary"
        data-test="cta-close"
        @click.prevent="onClose"
      >
        {{ t("components.review_platforms.cta_cancel") }}
      </AtomButton>
      <AtomButton
        size="large"
        data-test="cta-share"
        @click.prevent="handleShare"
      >
        {{ ctaLabel }}
      </AtomButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.review-platforms {
  display: flex;
  gap: var(--spacing-lg);
  flex-direction: column;

  &__platform {
    display: flex;
    gap: var(--spacing-md);
    list-style: none;
    margin: var(--spacing-sm) 0 0;
    padding: 0;
    overflow: auto;

    &-item {
      flex: 1;
      min-width: 120px;
    }

    &-icon {
      display: flex;
      height: 100%;
      align-items: center;
      flex-direction: column;
      justify-content: flex-end;
      flex: 1;
      padding: 7px;
      text-align: center;
      border-radius: var(--radii-card-sm);
      border: 1px solid var(--color-base-90);
      background: var(--color-base-100);
      transition: all 0.2s ease-out;

      &:hover {
        box-shadow: var(--elevation-level-1);
        background: var(--color-base-95);
      }

      &--active {
        cursor: default;
        font-weight: bold;
        background: var(--color-base-95);
      }
    }
  }

  &__comment {
    display: flex;
    gap: var(--spacing-md);
    align-items: center;
    justify-content: space-between;
    padding: var(--spacing-md);
    border-radius: var(--radii-card-sm);
    border: 1px solid var(--color-base-60);

    &-copy {
      display: flex;
    }
  }

  &__ctas {
    display: flex;
    gap: var(--spacing-lg);
    justify-content: flex-end;
  }
}
</style>
